import { useDocumentTitle } from "@/lib/use-document-title";
import { Link, Navigate } from "react-router-dom";
import { CustomerLayout } from "@/components/CustomerLayout";
import {
  useCurrentMember, useBenefitDefinitions, usePartners, useEvents, useStores,
  useBenefitUsageThisMonth, formatNextResetLabel, useMyAttendanceCount,
} from "@/lib/data-hooks";
import { Zap, Tag, Search, Gift, AlertCircle, MapPin, Star, Sparkles, ArrowRight, Users, CreditCard, Calendar, Banknote } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { toast } from "sonner";
import event1 from "@/assets/event-1.jpg";
import event2 from "@/assets/event-2.jpg";
import event3 from "@/assets/event-3.jpg";
import event4 from "@/assets/event-4.jpg";
import { OnboardingTour } from "@/components/OnboardingTour";


const eventImages = [event1, event2, event3, event4];
const iconMap = { tag: Tag, search: Search, zap: Zap, star: Star, handshake: Gift } as const;
const MONTHS = ["JAN","FEB","MAR","APR","MAJ","JUN","JUL","AUG","SEP","OKT","NOV","DEC"];

function Dashboard() {
  useDocumentTitle("Översikt — Lagamobilen Access");
  const member = useCurrentMember();
  const { data: benefits = [] } = useBenefitDefinitions({ onlyActive: true });
  const { data: partners = [] } = usePartners({ onlyActive: true });
  const { data: events = [] } = useEvents({ onlyActive: true, upcomingOnly: true });
  const { data: stores = [] } = useStores();
  const { data: usageLogs = [] } = useBenefitUsageThisMonth(member?.id ?? null);
  const myNights = useMyAttendanceCount();
  const [portalLoading, setPortalLoading] = useState(false);

  const hasSubscription = !!member?.stripe_subscription_id;

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: { environment: getStripeEnvironment(), returnUrl: `${window.location.origin}/access/dashboard` },
      });
      if (error || !data?.url) throw new Error(error?.message || "Kunde inte öppna portalen");
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPortalLoading(false);
    }
  };


  const storeMap = new Map(stores.map((s) => [s.id, s.name]));
  const unlimited = benefits.filter((b) => b.category === "unlimited");
  const limited = benefits.filter((b) => b.category === "limited");
  const usedIds = new Set(usageLogs.map((u: any) => u.benefit_definition_id ?? u.benefit_type).filter(Boolean));
  const unused = limited.filter((b) => !usedIds.has(b.id)).length;
  const resetLabel = formatNextResetLabel();




  const m: any = member;
  const missing: string[] = [];
  if (member) {
    if (!m.ssn) missing.push("personnummer");
    if (!m.address) missing.push("gatuadress");
    if (!m.zip) missing.push("postnummer");
    if (!m.city) missing.push("ort");
    if (!m.store_id) missing.push("favoritbutik");
    if (!m.phone_brand) missing.push("telefonmärke");
    if (!m.operator) missing.push("operatör");
    if (!m.source) missing.push("hur du hörde om Access");
    if (!m.customer_type) missing.push("kundtyp");
    if (!Array.isArray(m.interests) || m.interests.length === 0) missing.push("intressen");
  }
  const profileIncomplete = !!member && missing.length > 0;

  return (
    <CustomerLayout>
      {member && <OnboardingTour />}

      {profileIncomplete && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-7 shadow-2xl">
            <div className="size-12 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] flex items-center justify-center mb-4">
              <Sparkles size={22} />
            </div>
            <h2 className="!text-2xl font-display font-bold mb-2">Slutför din profil</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Innan du kan börja använda dina förmåner behöver vi några uppgifter. Det tar ca 2 minuter.
            </p>
            <div className="bg-muted/50 rounded-lg p-3 mb-5">
              <p className="text-xs text-muted-foreground mb-1.5 font-semibold uppercase tracking-wider">Saknas ({missing.length})</p>
              <p className="text-sm">{missing.join(", ")}</p>
            </div>
            <Link to="/access/aktivera" className="btn-pill w-full justify-center">
              Slutför nu <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}

      <div className="max-w-6xl">
        <div className="mb-6">
          <h1 className="!text-3xl lg:!text-[2rem]">
            {member ? <>Välkommen tillbaka, {member.first_name}.</> : <>Välkommen till Lagamobilen Access.</>}
          </h1>
          {member ? (
            <p className="text-muted-foreground mt-1">Du har <strong className="text-primary">{unused} oanvänd{unused === 1 ? "" : "a"} förmån{unused === 1 ? "" : "er"}</strong> denna månad – {resetLabel.toLowerCase()}.</p>
          ) : (
            <p className="text-muted-foreground mt-1">Logga in för att se dina personliga förmåner och bokningar.</p>
          )}
        </div>

        {limited[0] && (
          <div className="mb-8 rounded-2xl bg-gradient-to-br from-[var(--color-ink)] via-[#1a1a1a] to-[var(--color-ink)] text-white p-7 lg:p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--color-gold)] opacity-[0.07] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative grid lg:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="inline-flex items-center gap-1.5 text-[var(--color-gold)] text-[11px] uppercase tracking-widest font-semibold mb-3">
                  <Sparkles size={12} /> Veckans förmån
                </div>
                <h2 className="font-display font-bold text-2xl lg:text-3xl leading-tight text-white">{limited[0].name}</h2>
                <p className="text-white/70 text-sm mt-2 max-w-lg leading-relaxed">{limited[0].description}</p>
                <Link to="/access/formaner" className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] font-display font-semibold text-sm hover:opacity-90 transition">
                  Aktivera förmånen <ArrowRight size={14} />
                </Link>
              </div>
              <div className="lg:border-l lg:border-white/10 lg:pl-7">
                <div className="text-white/50 text-xs uppercase tracking-wider">Återställs</div>
                <div className="font-display font-bold text-[var(--color-gold)] text-3xl leading-tight mt-1">{resetLabel.replace("Återställs den 1:a ", "1:a ")}</div>
              </div>
            </div>
          </div>
        )}

        {member?.subscription_status === "past_due" && (
          <div className="mb-8 rounded-2xl border-2 border-amber-500/50 bg-amber-500/[0.08] p-6 lg:p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500 opacity-[0.07] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-5">
              <div className="w-14 h-14 shrink-0 rounded-full bg-amber-500/15 flex items-center justify-center">
                <AlertCircle className="text-amber-500" size={28} />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-lg text-amber-700">
                  Din betalning kunde inte dras
                </div>
                <p className="text-sm text-amber-800/80 mt-1 leading-relaxed">
                  Stripe misslyckades att debitera ditt betalkort. För att inte förlora dina medlemsförmåner behöver du uppdatera ditt betalsätt snarast. Klicka på knappen nedan för att gå till Stripe Customer Portal.
                </p>
              </div>
              <button
                onClick={openPortal}
                disabled={portalLoading}
                className="btn-pill whitespace-nowrap !text-sm inline-flex items-center gap-2 disabled:opacity-50 bg-amber-600 hover:bg-amber-700 text-white border-0"
              >
                <CreditCard size={16} />
                {portalLoading ? "Öppnar…" : "Uppdatera betalsätt"}
              </button>
            </div>
          </div>
        )}

        {member && unused > 0 && member?.subscription_status !== "past_due" && (
          <div className="flex items-start gap-3 p-4 mb-10 rounded-xl border border-[var(--color-gold)]/40 bg-[var(--color-gold)]/5">
            <AlertCircle className="text-[var(--color-gold)] shrink-0 mt-0.5" size={20} />
            <div className="text-sm">
              <strong className="font-display">Glöm inte aktivera!</strong> Du har förmåner du inte använt denna månad. <Link to="/access/formaner" className="text-primary underline">Gå till mina förmåner →</Link>
            </div>
          </div>
        )}

        <h2 className="mb-1">Dina medlemsförmåner</h2>
        <p className="text-muted-foreground text-sm mb-6">Som medlem i Lagamobilen Access får du tillgång till exklusiva förmåner och prioriterad service.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
          {unlimited.map(b => {
            const Icon = iconMap[b.icon as keyof typeof iconMap] ?? Star;
            return (
              <div key={b.id} className="card-soft p-5 text-center hover:-translate-y-0.5 hover:shadow-lg transition">
                <Icon className="text-primary mx-auto mb-3" size={28} />
                <div className="font-display font-semibold text-sm mb-1 break-words hyphens-auto" lang="sv">{b.name}</div>
                <p className="text-xs text-muted-foreground leading-snug">{b.description}</p>
              </div>
            );
          })}
        </div>

        <div className="mb-12 rounded-2xl border-2 border-dashed border-[var(--color-primary)]/40 bg-[var(--color-primary)]/5 p-6 flex flex-col md:flex-row md:items-center gap-5">
          <div className="w-14 h-14 shrink-0 rounded-full bg-[var(--color-primary)]/15 flex items-center justify-center">
            <Users className="text-[var(--color-primary)]" size={24} />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg">Bjud in en vän — få en gratis månad</div>
            <p className="text-sm text-muted-foreground mt-1">När din vän blir medlem får du och vännen en månad gratis.</p>
          </div>
          <Link to="/access/bjud-in" className="btn-pill whitespace-nowrap !text-sm">Bjud in nu</Link>
        </div>

        <div className="flex items-baseline justify-between mb-2">
          <h2>Erbjudanden från våra samarbetspartners</h2>
          <Link to="/access/erbjudanden" className="text-sm text-blue-600 hover:underline">Visa alla</Link>
        </div>
        <p className="text-muted-foreground text-sm mb-6">Som Access-medlem får du exklusiva rabatter hos våra partners.</p>
        <div className="grid md:grid-cols-2 gap-5 mb-12">
          {partners.slice(0, 2).map(p => (
            <div key={p.id} className="card-soft p-6 flex items-center gap-5">
              <div className="flex-1">
                <div className="font-display font-bold text-lg">{p.name}</div>
                <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
              </div>
              {p.discount_label && (
                <div className="w-16 h-16 shrink-0 rounded-full bg-primary text-white flex items-center justify-center font-display font-bold">{p.discount_label}</div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-baseline justify-between mb-6">
          <h2>Kommande event</h2>
          <Link to="/access/event" className="text-sm text-blue-600 hover:underline">Visa alla</Link>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mb-12">
          {events.slice(0, 4).map((e, i) => {
            const d = new Date(e.starts_at);
            const day = d.getDate().toString().padStart(2, "0");
            const month = MONTHS[d.getMonth()];
            const storeName = e.store_id ? storeMap.get(e.store_id) : "";
            return (
              <div key={e.id} className="card-soft overflow-hidden">
                <div className="relative h-36 bg-cover bg-center" style={{ backgroundImage: `url(${e.cover_image_url || eventImages[i % eventImages.length]})` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
                  <div className="absolute top-3 left-3 bg-white rounded-md px-3 py-1.5 text-center shadow">
                    <div className="font-display font-bold text-lg leading-none">{day}</div>
                    <div className="text-[10px] tracking-widest text-muted-foreground">{month}</div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="font-display font-semibold text-sm mb-2">{e.title}</div>
                  {storeName && <div className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin size={12} />{storeName}</div>}
                  <div className="text-xs text-muted-foreground mt-1">{d.toLocaleDateString("sv-SE", { dateStyle: "long" })}</div>
                  <Link to="/access/event" className="btn-pill-outline w-full mt-4 !py-2 !text-xs text-center block">Anmäl dig</Link>
                </div>
              </div>
            );
          })}
        </div>

        <h2 className="mb-4">Ditt digitala medlemskort</h2>
        <div className="rounded-2xl bg-gradient-to-br from-[var(--color-ink)] via-[#1a1a1a] to-[var(--color-ink)] text-white p-7 max-w-md shadow-xl relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 opacity-20">
            <svg width="220" height="220" viewBox="0 0 200 200"><path d="M20 180 L100 30 L180 180 Z" stroke="#C9922A" strokeWidth="2" fill="none" /></svg>
          </div>
          <div className="relative z-10">
            <div className="font-display font-light text-sm tracking-widest text-white/70">LAGAMOBILEN</div>
            <div className="font-display font-semibold text-[var(--color-gold)] text-2xl tracking-[0.18em]">ACCESS</div>
            <div className="mt-8">
              <div className="text-white/60 text-xs uppercase tracking-wider mb-1">Medlem</div>
              <div className="font-display font-bold text-xl">{member ? `${member.first_name} ${member.last_name}` : "—"}</div>
            </div>
            <div className="mt-3 text-sm text-white/80">
              Du har varit på <span className="font-display font-bold text-[var(--color-gold)]">{myNights}</span> {myNights === 1 ? "kväll" : "kvällar"}
            </div>
            <div className="mt-4 flex justify-between items-end">
              <div>
                <div className="text-white/60 text-xs uppercase tracking-wider">Medlemsnummer</div>
                <div className="font-mono text-sm">{member?.member_number ?? "—"}</div>
              </div>
              <div className="text-right">
                <div className="text-white/60 text-xs uppercase tracking-wider">Aktiv sedan</div>
                <div className="text-sm">{member?.member_since ?? "—"}</div>
              </div>
            </div>
          </div>
        </div>

        {hasSubscription && member?.current_period_end && (
          <div className="mt-6 max-w-md rounded-xl border border-[var(--color-gold)]/20 bg-[var(--color-gold)]/[0.04] p-5">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={16} className="text-[var(--color-gold)]" />
              <span className="font-display font-semibold text-sm">Betalningsinformation</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-muted-foreground mb-0.5">Nästa debitering</div>
                <div className="font-display font-bold text-base">
                  {new Date(member.current_period_end).toLocaleDateString("sv-SE", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-0.5">Belopp</div>
                <div className="font-display font-bold text-base flex items-center gap-1">
                  <Banknote size={16} className="text-[var(--color-gold)]" />
                  99 kr / mån <span className="text-xs font-normal text-muted-foreground">inkl. moms</span>
                </div>
              </div>
            </div>
            {member.subscription_status === "active" ? (
              <div className="mt-3 text-xs text-emerald-600 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                Prenumerationen är aktiv
              </div>
            ) : member.subscription_status === "past_due" ? (
              <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] p-3 flex items-start gap-2.5">
                <AlertCircle className="text-amber-500 shrink-0 mt-0.5" size={14} />
                <div>
                  <div className="text-xs font-semibold text-amber-700">
                    Betalning misslyckades
                  </div>
                  <div className="text-[11px] text-amber-800/80 mt-0.5 leading-relaxed">
                    Stripe försöker debitera igen automatiskt. Om problemet kvarstår kommer ditt medlemskap att avslutas. Klicka på <em className="font-semibold not-italic">"Hantera medlemskap"</em> nedan för att uppdatera betalsättet.
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {hasSubscription && (
          <div className="mt-4 max-w-md">
            <button
              onClick={openPortal}
              disabled={portalLoading}
              className="btn-pill-outline w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <CreditCard size={16} />
              {portalLoading ? "Öppnar…" : "Hantera medlemskap"}
            </button>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Uppdatera betalsätt, se kvitton eller säg upp ditt medlemskap.
            </p>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
}

export default Dashboard;
